/**
 * The legal documents, in one place.
 *
 * Single source of truth: the public /legal/:type route and the modal shown
 * over onboarding both read from here, so the text a reviewer opens from a
 * public URL and the text a user reads while signing up cannot drift apart.
 *
 * Terms and Privacy are the 24 September 2026 versions, written in Markdown —
 * "#"/"##" headings, "-" bullets and **bold**. The renderer in LegalScreen and
 * LegalModal is what turns that into headings and lists; splitting on newlines
 * and emitting paragraphs would print the "#" and "-" characters literally.
 *
 * Community Guidelines is a separate in-app document and is unchanged. The
 * rules it covers also appear as section 5 of the Terms.
 */

/**
 * The version of the Terms and Privacy Policy currently in force.
 *
 * Stamped onto the user document as `policyVersion` when someone accepts during
 * onboarding. Bumping it is what re-prompts existing users: anyone whose stored
 * value is missing or older than this has not agreed to the current text.
 *
 * It lives beside the documents because the two must change together.
 */
export const TERMS_VERSION = '2026-09-24';

export type LegalDocumentType = 'privacy' | 'terms' | 'guidelines';

export const TITLES: Record<LegalDocumentType, string> = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  guidelines: 'Community Guidelines',
};

export const CONTENTS: Record<LegalDocumentType, string> = {
  terms: `# AfroYap Terms of Service

**Effective Date: September 24, 2026**

## 1. Agreement to Terms

Welcome to AfroYap ("AfroYap," "we," "our," or "us"), operated by Muhammed Jatta in Colorado, United States.

By creating an account or using AfroYap on any platform (iOS, Android, or web), you agree to these Terms of Service and our Privacy Policy. If you do not agree, do not use AfroYap.

## 2. Eligibility

You must be at least 16 years old to use AfroYap. If you are 16 or 17, you confirm that a parent or legal guardian has reviewed these Terms with you.

Users aged 16 and 17 receive additional protections described in our Privacy Policy, including limited visibility in search.

If we learn that a user is under 16, we will remove the account and its associated data. You may not provide false information about your age, including your date of birth.

## 3. Accounts

- Only one account per person is permitted.
- You must provide accurate information and keep your login credentials secure. You are responsible for activity on your account.
- Usernames may be changed once every 30 days. We may reclaim or change a username that impersonates another person, infringes a trademark, or violates these Terms.
- We may remove accounts that have been inactive for 12 months.

## 4. Your Content

You keep ownership of the content you post on AfroYap, including Yaps, Shouts, replies, polls, images, videos, and profile information.

By posting content, you grant AfroYap a non-exclusive, worldwide, royalty-free license to host, store, display, format, and distribute that content in order to operate and provide AfroYap.

For publicly shared content only, you also grant us permission to feature it in promotional materials for AfroYap, including on our social media accounts. We will not use the following in marketing:

- Direct messages
- Posts in private or approval-only tribes and groups
- Content posted by users under 18

This license ends when you delete your content or your account, except where content has already been featured in marketing materials, or where we must keep it for legal or safety reasons.

You are responsible for the content you post and confirm you have the rights to share it.

## 5. Community Rules

AfroYap is a community for the Black and African diaspora. The following are prohibited:

- Hate speech, including attacks based on race, ethnicity, national origin, religion, sex, gender, sexual orientation, or disability
- Harassment, bullying, or threats
- Encouraging or glorifying violence
- Promoting self-harm or suicide
- Impersonating another person or organization
- Doxxing (sharing someone's private personal information)
- Sexually explicit content
- Spam, scams, fraud, or misleading schemes
- Selling unauthorized goods or services
- Posting content that infringes someone else's copyright or trademark
- Illegal activity, or content promoting it
- Creating accounts to evade a suspension or ban
- Using bots, scripts, or scraping tools, or exploiting bugs, including in the Gold system
- Abusing the reporting system with false reports

**Child safety: zero tolerance.** Any sexual content involving minors, sexualizing minors, grooming, or an adult pursuing a romantic or sexual relationship with a minor will result in immediate, permanent termination. Where required by law, we report it to the appropriate authorities.

AfroYap has zero tolerance for objectionable content and abusive users.

## 6. Reporting, Blocking, and Moderation

You can report content or accounts and block other users from within the app. Blocked users can no longer see your content or interact with you, and you won't see theirs.

We review reports and may remove content, restrict features, suspend accounts, or permanently terminate accounts. Community moderators may remove content and issue suspensions but cannot permanently ban users. Permanent bans are decided by platform administration.

If your account is suspended, you may appeal by contacting afroyapteam@gmail.com. We aim to review appeals within 14 days.

## 7. Tribes, Forums, and Groups

Users can create and join tribes, forums, and groups. Some are open to anyone; others require approval or an invitation.

Creators and administrators of tribes and groups are responsible for keeping them within these Terms. AfroYap may remove any tribe, forum, or group that violates these Terms.

Content in private or approval-only spaces is limited to members, but members can still see, save, or share what you post there, so share thoughtfully.

## 8. Verification Badge

The verified badge shows that an account has completed email and phone verification. It does not mean AfroYap endorses the account. We may remove a badge if verification details change or the account violates these Terms.

## 9. Gold

AfroYap includes a virtual currency called Gold. Gold is currently earned within the app and cannot be purchased with real money.

Gold has no cash value, cannot be redeemed for money, and cannot be transferred or sold outside AfroYap. We may adjust balances, prices, and rules for Gold at any time, including correcting balances gained through bugs or misuse. Gold is forfeited when an account is deleted or permanently terminated.

If we introduce paid features or Gold purchases in the future, additional terms will apply and we will update these Terms.

## 10. Copyright

We respect intellectual property rights. If you believe content on AfroYap infringes your copyright, email afroyapteam@gmail.com with a description of the work, a link to or description of the content, your contact information, and a statement that you believe in good faith the use is unauthorized. We may remove infringing content and terminate accounts of repeat infringers.

## 11. Account Deletion and Termination

You can delete your account at any time from within the app. We may suspend or terminate accounts that violate these Terms or put the community at risk. In serious cases, this may happen without notice.

## 12. Disclaimers

AfroYap is provided "as is" and "as available." Features may change or be removed over time. We do not guarantee that AfroYap will be uninterrupted, error-free, or that content will always be preserved.

## 13. Limitation of Liability

To the maximum extent permitted by law, AfroYap is not liable for user content, disputes between users, or any indirect, incidental, special, or consequential damages. Our total liability for any claim relating to AfroYap will not exceed $100.

## 14. Indemnification

You agree to cover AfroYap against claims, losses, or costs arising from your content or your violation of these Terms, to the extent permitted by law.

## 15. App Store Terms

If you downloaded AfroYap from the Apple App Store:

- These Terms are between you and AfroYap, not Apple. Apple is not responsible for AfroYap or its content.
- Apple has no obligation to provide maintenance or support for AfroYap.
- Apple is not responsible for any product claims, legal compliance claims, or intellectual property claims relating to AfroYap.
- You confirm you are not located in a country subject to a US government embargo and are not on any US government list of restricted parties.
- Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you.

Your use of AfroYap must also follow the terms of the app store you downloaded it from.

## 16. Changes to These Terms

We may update these Terms. For significant changes, we will notify you in the app before they take effect. Continuing to use AfroYap after changes take effect means you accept the updated Terms.

## 17. Governing Law

These Terms are governed by the laws of the State of Colorado, United States, without regard to conflict-of-law rules.

## 18. General

If any part of these Terms is found unenforceable, the rest remains in effect. These Terms and our Privacy Policy are the entire agreement between you and AfroYap. Our failure to enforce any part is not a waiver.

## 19. Contact

For questions, appeals, or legal concerns: afroyapteam@gmail.com
`,

  privacy: `# AfroYap Privacy Policy

**Effective Date: September 24, 2026**

AfroYap ("AfroYap," "we," "our," or "us") is operated by Muhammed Jatta in Colorado, United States. This Privacy Policy explains what information we collect when you use AfroYap on iOS, Android, or the web, how we use and share it, and the choices you have.

## 1. Information We Collect

**Information you provide**

- **Account information:** username, password (stored securely by our authentication provider, never in readable form), and date of birth.
- **Optional contact information:** email address and phone number, if you add them for verification or sign in with Google.
- **Profile information:** profile photo, bio, heritage/country, and where you live, if you choose to add it.
- **Content:** Yaps, Shouts, replies, polls, images, videos, tribe and forum posts, and direct messages.
- **Interactions:** likes, follows, friend connections, tribe memberships, blocks, and reports.
- **Gold:** your balance and how it is earned and spent.

**Information collected automatically**

- **Device and technical information:** device type, operating system, app version, and crash and error logs.
- **Activity information:** when you were last active, when you typically open the app, your time zone, and how you use features.
- **Push notification tokens:** so we can send you notifications.

**Information from others**

- If you sign in with Google, we receive your name, email address, and account identifier from Google.

## 2. How We Use Your Information

We use your information to:

- Create and manage your account and verify your identity
- Provide AfroYap's features, including feeds, tribes, messaging, and Gold
- Send notifications, including activity recaps
- Keep the community safe, enforce our Terms, review reports, and apply age-based protections
- Measure how people find AfroYap and improve the app
- Comply with legal obligations

## 3. How We Share Your Information

We do not sell your personal information. We share information only as follows:

- **With other users:** your profile and content are visible according to where you post and your settings (see Section 4).
- **With service providers** that help run AfroYap, including Google Firebase (hosting, database, authentication, and notifications) and our email delivery provider.
- **With moderators:** community moderators can see reported content and account information needed to enforce our Terms.
- **For legal reasons:** when required by law, or to protect the safety of users or the public.
- **If AfroYap is transferred:** as part of a merger, acquisition, or sale of assets, subject to this Policy.

We do not share your information with advertising networks. AfroYap does not track you across other apps or websites and does not use advertising identifiers.

## 4. Who Can See Your Content

- **Public content** (such as Yaps on the main feed and your public profile) can be seen by anyone using AfroYap, and some public content may be visible to signed-out visitors on the web.
- **Private and approval-only tribes and groups:** posts are visible only to members.
- **Direct messages:** visible only to you and the people in the conversation, plus moderators when a message is reported.
- **Blocked users** cannot see your content.

## 5. Users Aged 16 and 17

AfroYap is for users 16 and older. Users aged 16 and 17 receive additional protections, including limited visibility in search, and their content is never used in our marketing. We do not share their information for advertising purposes. If we learn that a user is under 16, we will delete the account and its data.

## 6. Your Choices and Rights

- **Access and correction:** you can view and edit most of your information in the app. To request a copy of your data, email us.
- **Deletion:** you can delete your account from within the app (see Section 8).
- **Notifications:** you can turn off push notifications in the app or in your device settings.

Depending on your state or country, you may have additional privacy rights, including the right to access, correct, delete, or receive a copy of your data, and to opt out of certain sharing. This includes residents of states with privacy laws, such as California and Colorado, and users in the European Union, United Kingdom, and Canada. To use these rights, email us. We will not treat you differently for exercising them.

## 7. Data Retention

We keep your information while your account is active. When you delete your account, we remove your profile and content from AfroYap within 30 days. Copies may remain in backups for up to 90 days. We may keep limited information longer when needed for safety or legal reasons, such as records of permanent bans to prevent ban evasion.

## 8. Account Deletion

You can delete your account at any time from within the app. Deleting your account removes your profile, your content, and the direct messages you sent, for everyone in the conversation, subject to the retention limits in Section 7.

## 9. Security

We use reputable providers and reasonable safeguards to protect your information. No system is completely secure, so we cannot guarantee absolute security.

## 10. International Users

AfroYap is operated in the United States, and your information is stored and processed in the United States. By using AfroYap, you understand that your information will be transferred to the United States.

## 11. Changes to This Policy

We may update this Privacy Policy. For significant changes, we will notify you in the app before they take effect.

## 12. Contact Us

For privacy questions or requests: afroyapteam@gmail.com
`,

  guidelines: `# AfroYap Community Guidelines

Our goal is to create a vibrant, safe, and welcoming community for everyone.

## 1. Be Respectful

Treat everyone with respect. Healthy debates are natural, but kindness and humility is required to create an environment that benefits all parties regardless of opinion, nationality, ethnicity, etc.

## 2. No Hate Speech or Bullying

Make sure everyone feels safe. Bullying or harassment of any kind isn't allowed, and degrading comments about things like race, religion, culture and sexual orientation will not be tolerated.

## 3. No Excessive Promotion or Spam

We all are working to connect the diaspora, if you are working on something interesting please share it in the tribe or forum that it would correspond to. The YapTab is not the place to be promoting your personal projects.

## 4. No Encouragement of Violence

Any encouragement of violence or violent acts towards any group, person or organization is not allowed on the website whatsoever. Keep this in mind when making posts or communicating with others on the app.

## 5. No Explicit Posting

Explicit posts of an adult nature are not allowed on the platform. This is to keep the integrity of a productive community upheld. Posting any NSFW or explicit content will result in post or account deletion and moderation.

## 6. Respect Privacy & No Doxxing

Being part of this app requires mutual trust. Authentic, expressive discussions make connection great, but may also be sensitive and private. Respect everyone's privacy. Doxxing is not allowed under any circumstances and will result in immediate bans.

## 7. Respect Intellectual Property

Only post content that you have the right to post. Respect the intellectual property rights of others. Impersonation is not allowed under any circumstances.

## Reporting Violations

If you see something that you think breaks our guidelines, please report it to our moderation team. Use the platform's reporting features to flag content or users for moderation. You should also block users who persist in behavior you do not want to see in order to limit the reach of those specific posts.
`,
};
